import { useTranslation } from 'react-i18next';
import { CalendarIcon, PencilSquareIcon } from '@heroicons/react/16/solid';

import { FC, Fragment, useEffect, useRef, useState } from 'react';
import Modal from 'components/Modal';
import { requestFlightTicketProposalExtension } from 'api/Flights/sdk.ts';
import Tooltip from 'components/Tooltip';
import { IFlightTicketProposal } from 'entities/Flights/sdk.ts';
import { KeyedMutator } from 'swr';
import { cn } from 'utils/utils.ts';
import { Button } from 'components/ui/button.tsx';
import { H2 } from 'components/ui/typography.tsx';

const MAX_CHARACTERS = 2000;

const formatedDate = ({ dateString }: { dateString: string }) => {
  const dateObj = new Date(dateString);

  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(dateObj);
};

interface IOfferDetailsSectionProps {
  flightTicketProposal: IFlightTicketProposal;
  flightTicketProposalMutate: KeyedMutator<IFlightTicketProposal>;
}

const OfferDetailsSection: FC<IOfferDetailsSectionProps> = ({ flightTicketProposal, flightTicketProposalMutate }) => {
  const [open, setOpen] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [characters, setCharacters] = useState<string>('');
  const status = flightTicketProposal?.status;

  const isExtensionRequested = flightTicketProposal?.is_extension_requested ?? false;
  useEffect(() => {
    if (open && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [open]);

  const { t } = useTranslation();

  return (
    <div>
      <div className='max-lg:flex max-lg:flex-col max-lg:items-center'>
        <H2>{t('flightOffer.offerDetails')}</H2>
      </div>
      <div className='px-[10px] py-[15px] rounded-[12px] shadow-[4px_4px_4px_4px_rgba(0,0,0,0.04)] flex justify-between'>
        <div className='grow flex items-center gap-[6px] '>
          <CalendarIcon className='size-8 max-lg:size-6 fill-primary max-sm:hidden' />
          <div className='font-[MuseoSansCyrl] text-primary text-[30px] max-lg:text-[20px]'>
            {t('flightOffer.availableUntil')}: {formatedDate({ dateString: flightTicketProposal.expiration_datetime })}
          </div>
        </div>
        {status === 'Pending' && (
          <Fragment>
            <Tooltip show={isExtensionRequested} text={t('flightOffer.requestSent')}>
              <Button
                disabled={isExtensionRequested}
                className='w-[320px] flex gap-[8px] items-center justify-center max-lg:w-fit max-sm:hidden'
                onClick={() => setOpen(true)}
              >
                {t('flightOffer.requestExtension')}
              </Button>
            </Tooltip>
            <button disabled={isExtensionRequested} onClick={() => setOpen(true)}>
              <PencilSquareIcon
                className={`size-6 sm:hidden ${isExtensionRequested ? 'fill-gray-300' : 'fill-primary'}`}
              />
            </button>
          </Fragment>
        )}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('flightOffer.requestExtension')}
        subtitle={t('flightOffer.requestExtensionDescription')}
        content={
          <div>
            <p className='font-[MuseoSansCyrl] text-sm text-gray-600'>{t('flightOffer.message')}</p>
            <textarea
              value={characters}
              onChange={(event) => setCharacters(event.target.value)}
              maxLength={MAX_CHARACTERS}
              ref={textareaRef}
              className='border-1 border-gray-400 w-full rounded-sm'
              rows={4}
            ></textarea>
            <p
              className={cn(
                'font-[MuseoSansCyrl] text-sm text-gray-600 mt-2',
                characters.length === MAX_CHARACTERS && 'text-red font-[MuseoSans900]'
              )}
            >
              {t('flightOffer.charactersLeft')}: {MAX_CHARACTERS - characters.length}
            </p>
          </div>
        }
        buttons={
          <div className='w-full'>
            <div className='flex justify-end'>
              <Button
                onClick={() => {
                  requestFlightTicketProposalExtension({
                    flightTicketProposalUUID: flightTicketProposal.uuid,
                    extensionNote: textareaRef?.current?.value,
                  }).then(() => {
                    setOpen(false);
                    flightTicketProposalMutate();
                  });
                }}
              >
                {t('common.confirm')}
              </Button>
            </div>
          </div>
        }
      ></Modal>
    </div>
  );
};

export default OfferDetailsSection;
